"use client";

import { useCallback, useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";

export type AnalyticsEventName =
  | "page_view"
  | "click"
  | "search"
  | "enroll"
  | "follow"
  | "save"
  | "unsave"
  | "create"
  | "complete_task"
  | "ai_message"
  | "error";

export interface AnalyticsPayload {
  [key: string]: string | number | boolean | null | undefined;
}

function emit(event: AnalyticsEventName, payload: AnalyticsPayload = {}): void {
  if (typeof window === "undefined") return;

  const body = {
    event,
    path: window.location.pathname,
    ts: new Date().toISOString(),
    ...payload,
  };

  // Console sink for development; production can replace with RUM / analytics provider.
  if (process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.debug("[analytics]", body);
  }

  try {
    const endpoint = process.env.NEXT_PUBLIC_ANALYTICS_URL;
    if (endpoint && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
      navigator.sendBeacon(endpoint, blob);
    }
  } catch {
    // Non-blocking: analytics must never break UX
  }

  try {
    window.dispatchEvent(new CustomEvent("hrshakti:analytics", { detail: body }));
  } catch {
    // ignore
  }
}

/**
 * Track page views and custom product events.
 * Designed to be provider-agnostic (console + optional beacon + CustomEvent).
 */
export function useAnalytics(options?: { trackPageViews?: boolean }) {
  const trackPageViews = options?.trackPageViews ?? true;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastPath = useRef<string>("");

  useEffect(() => {
    if (!trackPageViews || !pathname) return;
    const qs = searchParams?.toString();
    const full = qs ? `${pathname}?${qs}` : pathname;
    if (full === lastPath.current) return;
    lastPath.current = full;
    emit("page_view", { path: full });
  }, [pathname, searchParams, trackPageViews]);

  const track = useCallback((event: AnalyticsEventName, payload?: AnalyticsPayload) => {
    emit(event, payload);
  }, []);

  return { track };
}

export function trackEvent(event: AnalyticsEventName, payload?: AnalyticsPayload): void {
  emit(event, payload);
}
